import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { getLibraries, getLibraryById, LibrariesResponse } from "helpers/api";
import { useMemo } from "react";

const LIBRARIES_PAGE_SIZE = 50;

export function useLibraries(initialValue?: string) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["libraries", "active", "paginated"],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await getLibraries(
        undefined,
        LIBRARIES_PAGE_SIZE,
        pageParam
      );
      return response;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) {
        return undefined;
      }
      // Calculate the total number of items already fetched
      const totalFetched = allPages.reduce(
        (total, page) => total + page.libraries.length,
        0
      );
      return totalFetched;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (increased from 5)
    cacheTime: 30 * 60 * 1000, // 30 minutes (cache time - older property name)
    refetchOnWindowFocus: false, // Prevent refetching when window regains focus
    refetchOnMount: false, // Prevent refetching when component mounts if data exists
  });

  // Flatten all libraries from all pages with memoization
  const libraries = useMemo(() => {
    return (
      data?.pages.flatMap((page: LibrariesResponse) => page.libraries) ?? []
    );
  }, [data?.pages]);

  // If initialValue is provided and not found in current libraries, fetch it separately
  const shouldFetchInitialLibrary =
    Boolean(initialValue) &&
    !libraries.find((lib) => lib.library_id === initialValue) &&
    !isLoading;

  const { data: initialLibrary } = useQuery(
    ["library", initialValue],
    () => getLibraryById(initialValue!),
    {
      enabled: shouldFetchInitialLibrary,
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes (cache time - older property name)
      refetchOnWindowFocus: false,
    }
  );

  // Combine libraries with the initial library if it was fetched separately
  const allLibraries = useMemo(() => {
    if (initialLibrary) {
      // Check if initial library is already in the libraries array to avoid duplicates
      const exists = libraries.some(
        (lib) => lib.library_id === initialLibrary.library_id
      );
      return exists ? libraries : [initialLibrary, ...libraries];
    }
    return libraries;
  }, [libraries, initialLibrary]);

  return {
    libraries: allLibraries,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
  };
}
